import { useSuspenseQuery } from '@apollo/client'
import { Navigate, Outlet, useLocation, useMatches, useParams } from '@faceup/router'
import { usePromiseSuspense } from '@faceup/ui'
import { Spin } from '@faceup/ui-base'
import { convertBasenameToLanguage, languageEnumToBasename } from '@faceup/utils'
import { Suspense } from 'react'
import { graphql } from '../../../__generated__'
import { InstitutionLayout } from '../../../Components/InstitutionLayout/InstitutionLayout'
import ReportFormProvider from '../../../Contexts/ReportFormProvider'
import { SavedReportsProvider } from '../../../Contexts/SavedReportsProvider'
import { useCustomValues } from '../../../hooks/useCustomValues'
import type { LayoutVariant } from '../../../utils/constants'
import useRegion from '../../../utils/useRegion'
import { LanguageWrapper } from '../../LanguageWrapper'
import { ReportSourceProvider } from '../../ReportSourceProvider'
import { UiProvider } from '../../UiProvider'

type ReportLayoutHandle = {
  reportLayout: {
    variant?: LayoutVariant
    topAligned?: boolean
  }
}

/** Per-route layout config, carried on the route's `handle` and read via useMatches. */
export const reportLayoutHandle = (
  reportLayout: ReportLayoutHandle['reportLayout']
): ReportLayoutHandle => ({ reportLayout })

const isReportLayoutHandle = (handle: unknown): handle is ReportLayoutHandle =>
  typeof handle === 'object' && handle !== null && 'reportLayout' in handle

const query = graphql(`
  query ReportSourceInitByShortIdQuery(
    $shortId: GraphQLString!
    $language: Language!
  ) {
    publicReportSourceByShortId(id: $shortId) {
      password
      reportSource {
        languages
        defaultLanguage
        reportFormColor
        institution {
          id
          type
          isTesting
          billing {
            id
            plan
          }
          config {
            id
            institutionName
            primaryColor
          }
          partner {
            id
            config {
              id
              primaryColor
            }
          }
        }
        ...ReportSourceProvider_reportSource
        ...InstitutionLayout_reportSource
      }
    }
  }
`)

/**
 * Element of the report-source route branch (c|s/:shortId). Discovers the region,
 * runs the init query (app shell: provider + layout slices), narrows the language
 * to one the report source offers, then hands the shell data top-down. Pages run
 * their own content queries below the Outlet.
 *
 * Web flow for now; the app client (no discovery, hydrate from storage) is wired
 * in the mobile-isolation phase.
 */
export const ReportSourceWrapper = () => {
  const { language: languageParam, shortId } = useParams<'language' | 'shortId'>()
  const location = useLocation()
  const matches = useMatches()
  const { discoverByShortlink } = useRegion()
  // Live theming override from the akutan customization preview (postMessage).
  const customValues = useCustomValues()

  // Sets the module-level region before the regional query runs.
  const discovery = usePromiseSuspense(discoverByShortlink, [shortId ?? ''])

  // The language-dependent layout fields (pages, ...) are fetched up front with the
  // language from the URL. If it isn't offered by the source we redirect below and
  // the query refetches with the corrected language.
  const requestedLanguage = convertBasenameToLanguage(languageParam ?? '')

  const { data } = useSuspenseQuery(query, {
    variables: { shortId: shortId ?? '', language: requestedLanguage },
  })

  const result = data.publicReportSourceByShortId
  if (!discovery || !result) {
    return <Navigate to={routes => routes.home()} replace />
  }

  const reportSource = result.reportSource

  const effectiveLanguage = reportSource.languages.includes(requestedLanguage)
    ? requestedLanguage
    : reportSource.defaultLanguage

  // Compare basenames (not languages) so a garbage/unsupported first URL segment -
  // which convertBasenameToLanguage maps to the default - is normalized out of the
  // URL instead of left stuck there.
  const effectiveBasename = languageEnumToBasename(effectiveLanguage)
  if (effectiveBasename !== languageParam) {
    const segments = location.pathname.split('/')
    segments[1] = effectiveBasename
    const target = `${segments.join('/')}${location.search}`
    return <Navigate to={() => target} replace />
  }

  // Per-route layout config rides the route handle; deepest match wins.
  const layout = matches
    .map(match => match.handle)
    .filter(isReportLayoutHandle)
    .at(-1)?.reportLayout

  return (
    <LanguageWrapper language={effectiveLanguage}>
      <UiProvider
        primaryColor={
          customValues?.reportColor ??
          reportSource.reportFormColor ??
          reportSource.institution.config?.primaryColor ??
          reportSource.institution.partner?.config?.primaryColor ??
          undefined
        }
        institutionName={reportSource.institution.config?.institutionName ?? ''}
        mother={{
          type: reportSource.institution.type ?? null,
          isTesting: reportSource.institution.isTesting ?? null,
          plan: reportSource.institution.billing?.plan ?? null,
        }}
      >
        <ReportSourceProvider reportSource={result.reportSource} password={result.password ?? null}>
          {/* Report-session state, scoped to the report branch: victimPin handoff
              CreateReport -> ReportSent and the saved-reports list for check. Lives
              here (not globally) so it never leaks into survey/home surfaces. */}
          <SavedReportsProvider>
            <ReportFormProvider>
              <InstitutionLayout
                reportSource={result.reportSource}
                variant={layout?.variant}
                topAligned={layout?.topAligned}
              >
                <Suspense
                  fallback={
                    <div className='flex justify-center py-10'>
                      <Spin />
                    </div>
                  }
                >
                  <Outlet />
                </Suspense>
              </InstitutionLayout>
            </ReportFormProvider>
          </SavedReportsProvider>
        </ReportSourceProvider>
      </UiProvider>
    </LanguageWrapper>
  )
}
